/* import __COLOCATED_TEMPLATE__ from './edit-pii.hbs'; */
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { ApplicationFlowUpdate } from '@square/dashboard-protos/models/squareup/sfs/external/models/application_flow_update';
import { Address } from '@square/dashboard-protos/models/squareup/connect/v2/resources/address';
import { later } from '@ember/runloop';
import { scrollToFirstError } from '@square/ember-prism/utils/error-scrolling';
import STATE_CODES from '../utils/state-codes';
import { PO_BOX_REGEX, POSTAL_CODE_REGEX } from '../utils/regexes';
import moment from 'moment';
import { Error } from '@square/dashboard-protos/models/squareup/connect/v2/resources/error';
import { ReimaginedNavBankingVariations } from '../utils/launch-darkly-feature-flags';

// As of 22 June 2021, the GRG lists the oldest living American as Thelma Sutcliffe
// (born in Nebraska, October 1, 1906)
// -- https://en.wikipedia.org/wiki/List_of_American_supercentenarians
const MINIMUM_YEAR = 1906;

export default class EditPiiComponent extends Component {
  @service intl;
  @service addressValidation;
  @service currentApplicationFlow;
  @service features;

  @tracked isLoading = false;
  @tracked showValidations = false;

  @tracked firstName = '';
  @tracked lastName = '';

  @tracked line1 = '';
  @tracked line2 = '';
  @tracked city = '';
  @tracked state = null;
  @tracked zip = null;

  @tracked dob = {
    day: null,
    month: null,
    year: null,
  };
  @tracked ssn = null;
  @tracked ssnLast4 = null;
  @tracked ssnFailedServerValidation = false;

  @tracked gotLine2Input = false;

  // FIXME: in Octane, this should be done from this.args.individual in constructor()?
  didReceiveAttrs() {
    super.didReceiveAttrs();
    const individual = this.individual;
    if (individual) {
      this.firstName = individual.givenName || '';
      this.lastName = individual.surname || '';
      // map from Connect v2 Address
      this.line1 = individual.address?.addressLine1 || '';
      this.line2 = individual.address?.addressLine2 || '';
      this.city = individual.address?.locality || '';
      this.state = individual.address?.administrativeDistrictLevel1 || null;
      this.zip = individual.address?.postalCode || null;

      if (individual.birthDate) {
        const dob = new Date(individual.birthDate);
        this.dob = {
          day: dob.getUTCDate(),
          month: dob.getUTCMonth() + 1,
          year: dob.getUTCFullYear(),
        };
      }

      // We won't update this but it will be used to check if we already have an ssn on file
      this.ssnLast4 = individual.ssnLast4 || null;

      if (this.addressValidation.individualFailedValidation) {
        this.showValidations = true;
        this.scrollToError();
      }
    }
  }

  @computed(
    'city',
    'dob.{day,month,year}',
    'firstName',
    'isDobValid',
    'isSsnValid',
    'isZipValid',
    'lastName',
    'line1',
    'line2',
    'ssn',
    'state',
    'zip',
    'line1Validation',
    'line2Validation',
    'ssnFailedServerValidation'
  )
  get validations() {
    return {
      firstName: {
        isInvalid: this.firstName === '',
        message: this.intl.t('sfs.components.editPii.validation.firstName'),
      },
      lastName: {
        isInvalid: this.lastName === '',
        message: this.intl.t('sfs.components.editPii.validation.lastName'),
      },
      line1: this.line1Validation,
      line2: this.line2Validation,
      city: {
        isInvalid: this.city.trim() === '',
        message: this.intl.t('sfs.components.shared.validation.city'),
      },
      state: {
        isInvalid: !this.state,
        message: this.intl.t('sfs.components.shared.validation.state'),
      },
      zip: {
        isInvalid: !this.isZipValid,
        message: this.intl.t('sfs.components.shared.validation.zip'),
      },
      dob: {
        isInvalid: !this.isDobValid,
        message: this.intl.t('sfs.components.editPii.validation.dob'),
      },
      ssn: {
        isInvalid: !this.isSsnValid,
        message: this.intl.t('sfs.components.editPii.validation.ssn'),
      },
    };
  }

  get shouldSkipSplashPage() {
    return (
      this.features.getFlag('dashboard/reimagined_nav_banking_variations') ===
      ReimaginedNavBankingVariations.Test
    );
  }

  get isValid() {
    return Object.values(this.validations).every(
      ({ isInvalid, allowSubmit }) => allowSubmit || !isInvalid
    );
  }

  get isDobValid() {
    return this.dob.day && this.dob.month && this.dob.year;
  }

  get isSsnValid() {
    // Check explicitly because empty string is also falsey
    // The ssnLast4 will only be set if we already have an ssn on file so we want to just use that
    if (this.ssn === null && this.ssnLast4) {
      return true; // null ssn means you didn't edit it, that's fine
    } else {
      return this.ssn
        ?.trim()
        .replace(/[•-]/g, '')
        .match(/^\d{9}$/);
    }
  }

  @computed(
    'addressValidation.individualFailedFullAddressValidation',
    'isLoading'
  )
  get showFullAddressValidation() {
    return (
      this.addressValidation.individualFailedFullAddressValidation &&
      !this.isLoading
    );
  }

  get isZipValid() {
    return this.zip && this.zip.trim().match(POSTAL_CODE_REGEX);
  }

  get line1Validation() {
    if (PO_BOX_REGEX.test(this.line1.trim())) {
      return {
        isInvalid: true,
        message: this.intl.t('sfs.components.shared.validation.poBoxLine1'),
      };
    } else {
      return {
        isInvalid: this.line1.trim() === '',
        message: this.intl.t('sfs.components.shared.validation.line1'),
      };
    }
  }

  // Line2 requires frontend PO box validation which SHOULD block submit.
  // Line2 also displays validation messages from the backend about official address verification,
  // which should NOT block form submit.
  @computed(
    'line2',
    'addressValidation.individualFailedLine2Validation',
    'gotLine2Input',
    'isLoading'
  )
  get line2Validation() {
    if (PO_BOX_REGEX.test(this.line2.trim())) {
      return {
        isInvalid: true,
        message: this.intl.t('sfs.components.shared.validation.poBoxLine2'),
      };
    } else {
      return {
        isInvalid:
          this.addressValidation.individualFailedLine2Validation &&
          !this.gotLine2Input &&
          !this.isLoading,
        message: this.intl.t('sfs.components.shared.validation.validateLine2'),
        allowSubmit: true,
      };
    }
  }

  get stateOptions() {
    return STATE_CODES.map((stateCode) => {
      return { label: stateCode, value: stateCode };
    });
  }

  get yearOptions() {
    const MAXIMUM_YEAR = new Date().getFullYear() - 10;
    const years = [];
    years.push({ label: '', value: '' });
    for (let year = MAXIMUM_YEAR; year >= MINIMUM_YEAR; year--) {
      years.push({ label: year, value: year });
    }
    return years;
  }

  get monthOptions() {
    const months = [];
    months.push(''); // empty option
    for (let value = 1; value <= 12; value++) {
      const date = new Date(2020, value - 1, 1);
      const label = this.intl.formatDate(date, { month: 'short' });
      months.push({
        value,
        label,
      });
    }
    return months;
  }

  @computed('dob.{month,year}')
  get dayOptions() {
    const days = [];
    days.push(''); // empty option
    if (this.dob.month) {
      const month = this.dob.month - 1;
      const date = new Date(this.dob.year, month, 1);
      while (date.getMonth() === month) {
        const day = date.getDate();
        days.push({
          label: day,
          value: day,
        });
        date.setDate(day + 1);
      }
    } else {
      for (let i = 0; i < 31; i++) {
        days.push({
          label: i,
          value: i,
        });
      }
    }

    return days;
  }

  get newBirthDate() {
    const date = new Date(this.dob.year, this.dob.month - 1, this.dob.day);
    // YYYY-MM-DD for API Style Guide:
    const dateString = moment(date).format('YYYY-MM-DD');

    if (dateString === this.individual?.birthDate) {
      return null;
    } else {
      return dateString;
    }
  }

  get newAddress() {
    return new Address.Builder()
      .addressLine1(this.line1)
      .addressLine2(this.line2)
      .locality(this.city)
      .administrativeDistrictLevel1(this.state)
      .postalCode(this.zip)
      .build();
  }

  get newIndividual() {
    const builder = new ApplicationFlowUpdate.IndividualUpdate.Builder()
      .id(this.individual?.id)
      .givenName(this.firstName)
      .surname(this.lastName)
      .address(this.newAddress)
      .isManager(this.individual?.isManager)
      .hasSignificantOwnership(this.individual?.hasSignificantOwnership);

    if (this.newBirthDate) {
      builder.birthDate(this.newBirthDate);
    }

    if (this.ssn) {
      builder.ssn(this.ssn);
    }

    return builder.build();
  }

  get showDidFailIdv() {
    return this.currentApplicationFlow.didFailIdv && this.doNotPrefill;
  }

  get showSsnServerError() {
    return this.ssnFailedServerValidation && this.doNotPrefill;
  }

  @action
  line2Input() {
    this.gotLine2Input = true;
  }

  hasSsnFieldError(error) {
    return error?.protoMessage?.errors?.find((e) => {
      return (
        e.code === Error.Code.INVALID_VALUE &&
        e.category === Error.Category.INVALID_REQUEST_ERROR &&
        e.field === 'application_flow.individual.ssn'
      );
    });
  }

  scrollToError() {
    // This is in a runloop because the error classes have not yet rendered
    later(() => {
      // Tests can remove the elements before `later` fires
      const modal = document.querySelector('.sfs-edit-pii__modal');
      if (modal) {
        scrollToFirstError(modal);
      }
    });
  }

  // The magic of Ember binds this automatically to the form's submit event
  async submit(event) {
    event.preventDefault();
    // reset server check so that isValid() should pass:
    this.ssnFailedServerValidation = false;

    this.showValidations = true;

    if (this.isValid) {
      this.isLoading = true;
      try {
        await this.onUpdateIndividual(this.newIndividual);
      } catch (error) {
        if (this.hasSsnFieldError(error)) {
          this.ssnFailedServerValidation = true;
          this.scrollToError();
        }
      } finally {
        this.isLoading = false;
      }
    } else {
      this.scrollToError();
    }
  }
}
